import React, { useState, useEffect } from 'react';
import { Checkbox, FormControlLabel, FormGroup, Button, Typography, Box, Grid, CircularProgress } from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const AreasOfInterest = ({ userId }) => {
    const [areas, setAreas] = useState([]);  // List of available areas of interest from backend
    const [selectedAreas, setSelectedAreas] = useState([]);  // List of areas selected by the user
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);  // Track whether data has been submitted

    useEffect(() => {
        axios.get(`${API_URL}/api/areas-of-interest/`)  // Use the new GET endpoint for all areas
            .then(response => {
                setAreas(response.data || []);  // Set the list of areas available
                setLoading(false);
            })
            .catch(err => {
                setError("Error fetching areas of interest.");
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (userId) {
            axios.get(`${API_URL}/user_details_areas_of_interests/`, { params: { user_id: userId } })
                .then(response => {
                    const userSelectedAreas = response.data.areas_of_interest || [];
                    setSelectedAreas(userSelectedAreas);  // Set user's current selection
                    if (userSelectedAreas.length > 0) {
                        setSubmitted(true);  // Set submitted flag if the user has selected areas
                    }
                })
                .catch(err => {
                    setError("Error fetching user's selected areas.");
                });
        }
    }, [userId]);

    const handleCheckboxChange = (event) => {
        const { value, checked } = event.target;
        const areaName = value;  // Store the name of the area

        setSelectedAreas(prevSelected => {
            if (checked) {
                return [...prevSelected, areaName];  // Add selected area by name
            } else {
                return prevSelected.filter(area => area !== areaName);  // Remove unselected area by name
            }
        });
    };

    const handleSubmit = () => {
        const areasToSubmit = selectedAreas.filter(area => typeof area === 'string');  // Filter out non-string values like ids

        axios.patch(`${API_URL}/user_details_areas_of_interests/update/`, {
            user_id: userId,  // Send the user_id
            areas_of_interest: areasToSubmit  // Send the names of the selected areas (only names)
        })
            .then(response => {
                setSubmitted(true);  // Set the submitted flag
                alert("Areas of interest updated successfully!");
            })
            .catch(err => {
                alert("Error updating areas of interest.");
            });
    };

    if (loading) {
        return <CircularProgress sx={{ color: '#ff8c00' }} />;
    }

    if (error) {
        return <Typography sx={{ color: '#ff4444', textAlign: 'center', fontSize: '1rem' }}>{error}</Typography>;
    }

    return (
        <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 3 }}>
            <Typography sx={{ fontSize: '2rem', marginBottom: 3, textAlign: 'center', color: '#e0e0e0', letterSpacing: 5 }}>
                {submitted ? 'Your Selected Areas of Interest' : 'Select Your Areas of Interest'}
            </Typography>

            {submitted ? (
                <Box>
                    <Typography variant="h6" sx={{ color: '#e0e0e0', marginBottom: 2, letterSpacing: 5 }}>
                        Your selected areas are:
                    </Typography>
                    <Grid container spacing={2} sx={{ justifyContent: 'center', letterSpacing: 4 }}>
                        {areas.map((area, index) => (
                            <Grid item xs={12} key={index}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            value={area.name}
                                            onChange={handleCheckboxChange}
                                            checked={selectedAreas.includes(area.name)}
                                            sx={{
                                                color: '#ffffff',
                                                '&.Mui-checked': { color: '#ff8c00' },
                                                '&:hover': { backgroundColor: 'rgba(255, 140, 0, 0.1)' },
                                            }}
                                        />
                                    }
                                    label={area.name}
                                    sx={{
                                        color: '#e0e0e0',
                                        fontSize: '1.1rem',
                                        '& .MuiFormControlLabel-label': {
                                            letterSpacing: 3, // 👈 This applies spacing to the actual label text
                                        },
                                    }}
                                />

                            </Grid>
                        ))}
                    </Grid>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            marginTop: 3,
                        }}
                    >
                        <Button
                            sx={{
                                backgroundColor: '#ff8c00',
                                color: '#fff',
                                fontWeight: 'bold',
                                padding: 1.5,
                                borderRadius: '30px',
                                marginTop: 3,
                                letterSpacing: 4,
                                '&:hover': { backgroundColor: '#ff6f00' },
                            }}
                            onClick={handleSubmit}
                        >
                            Save Changes
                        </Button>
                    </Box>

                </Box>
            ) : (
                <Grid container spacing={2} sx={{ justifyContent: 'center', letterSpacing: 4, }}>
                    <Grid item xs={12}>
                        <FormGroup>
                            {areas.map((area, index) => (
                                <FormControlLabel
                                    key={index}
                                    control={
                                        <Checkbox
                                            value={area.name}
                                            onChange={handleCheckboxChange}
                                            checked={selectedAreas.includes(area.name)}
                                            sx={{
                                                color: '#ffffff',
                                                '&.Mui-checked': { color: '#ff8c00' },
                                                '&:hover': { backgroundColor: 'rgba(255, 140, 0, 0.1)' },
                                            }}
                                        />
                                    }
                                    label={area.name}
                                    sx={{
                                        color: '#e0e0e0',
                                        fontSize: '1.1rem',
                                        '& .MuiFormControlLabel-label': {
                                            letterSpacing: 3, // <- Adjust this value as needed
                                            fontSize: '1rem',
                                        },
                                    }}
                                />
                            ))}
                        </FormGroup>

                    </Grid>
                </Grid>
            )}

            {!submitted && (

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: 3,
                    }}
                >
                    <Button
                        sx={{
                            backgroundColor: '#ff8c00',
                            color: '#fff',
                            padding: 1.5,
                            borderRadius: '30px',
                            letterSpacing: 4,
                            '&:hover': { backgroundColor: '#ff6f00' },
                        }}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default AreasOfInterest;
